import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BookmarkBorder as SaveTemplateIcon, Search as SearchIcon } from '@mui/icons-material';
import PatientProfileAvatar from '../PatientProfileAvatar';
import NotificationDialog from '../NotificationDialog';
import {
  listMetahumanCharacterOptions,
  getIntegratedPatientTemplate,
  listMyPatientTemplates,
  createMyPatientTemplate,
} from '../../utils/userContentApi';
import {
  ANATOMAGE_PATIENT,
  ANATOMAGE_PATIENTS,
} from '../../utils/myPatients';
import {
  detailLines,
  filterCharacterOptions,
  findCharacterOption,
} from '../../utils/metahumanCharacterSearch';

function optionLabel(option) {
  if (!option) return '';
  if (option.source === 'saved') return option.name?.trim() || 'Saved template';
  const name = option.name?.trim() || ANATOMAGE_PATIENT;
  const bits = detailLines(option).slice(0, 3).map((line) => line.value);
  return bits.length ? `${name} (${bits.join(' · ')})` : name;
}

const SECTION_COPY = {
  health: {
    title: 'Replace health profile?',
    savedBody: (name) =>
      `Apply "${name}" to this patient? All health tab fields will be replaced with your saved template.`,
    anatomageBody: (name) =>
      `Apply ${name} as a health profile template? All health tab fields will be replaced. Mapped fields come from the ${ANATOMAGE_PATIENT}; fields without a template mapping (substance use, diet, obstetrical health, mental health, and others) will be cleared.`,
  },
  pathology: {
    title: 'Replace pathology?',
    savedBody: (name) =>
      `Apply "${name}" to this patient? All pathology tab fields will be replaced with your saved template.`,
    anatomageBody: (name) =>
      `Apply ${name} as a pathology template? All pathology tab fields will be replaced. Mapped fields come from the ${ANATOMAGE_PATIENT}; stage, complication, and any fields without a mapping will be cleared.`,
  },
};

function savedTemplateToOption(template) {
  return {
    source: 'saved',
    templateId: template.id,
    name: template.name,
    health: template.health,
    pathology: template.pathology,
  };
}

/**
 * Apply an Anatomage Patient or a saved health/pathology template to the editor.
 */
export default function PatientTemplatePicker({
  title,
  description,
  section = 'health',
  onApply,
  onError,
  savePayload,
}) {
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [savedLoadError, setSavedLoadError] = useState('');
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);

  const reloadSaved = useCallback(() => {
    setLoadingSaved(true);
    return listMyPatientTemplates({ type: section, limit: 100 })
      .then((payload) => {
        const rows = Array.isArray(payload?.templates) ? payload.templates : [];
        setSavedTemplates(rows);
        setSavedLoadError('');
      })
      .catch((err) => {
        setSavedTemplates([]);
        setSavedLoadError(err.message || 'Your templates could not be loaded.');
      })
      .finally(() => setLoadingSaved(false));
  }, [section]);

  useEffect(() => {
    let cancelled = false;
    reloadSaved().then(() => {
      if (cancelled) return undefined;
      return undefined;
    });
    return () => {
      cancelled = true;
    };
  }, [reloadSaved]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    listMetahumanCharacterOptions()
      .then((payload) => {
        if (cancelled) return;
        setOptions(Array.isArray(payload?.options) ? payload.options : []);
        setLoadError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setLoadError(err.message || `${ANATOMAGE_PATIENTS} could not be loaded.`);
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const savedOptions = useMemo(
    () => savedTemplates.map(savedTemplateToOption),
    [savedTemplates]
  );

  const filteredSaved = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return savedOptions;
    return savedOptions.filter((option) => optionLabel(option).toLowerCase().includes(q));
  }, [savedOptions, search]);

  const filteredOptions = useMemo(
    () => filterCharacterOptions(options, search).map((option) => ({ ...option, source: 'anatomage' })),
    [options, search]
  );

  const combinedOptions = useMemo(
    () => [...filteredSaved, ...filteredOptions],
    [filteredSaved, filteredOptions]
  );

  const handleSelect = (_event, option, reason) => {
    if (reason === 'clear') {
      setSelected(null);
      return;
    }
    if (!option) return;
    if (option.source === 'saved') {
      setConfirm({ option, kind: 'saved' });
      return;
    }
    if (!option.personaId) return;
    setConfirm({ option, kind: 'anatomage' });
  };

  const closeConfirm = () => {
    if (!applying) setConfirm(null);
  };

  const confirmApply = async () => {
    const option = confirm?.option;
    if (!option) {
      setConfirm(null);
      return;
    }
    setApplying(true);
    try {
      if (confirm.kind === 'saved') {
        onApply({
          source: 'saved',
          name: option.name,
          health: option.health,
          pathology: option.pathology,
        });
        setSelected(option);
        setConfirm(null);
        return;
      }
      if (!option.personaId) {
        setConfirm(null);
        return;
      }
      const payload = await getIntegratedPatientTemplate(option.personaId);
      onApply({ source: 'anatomage', ...payload, name: payload.name || option.name });
      setSelected({ ...option, source: 'anatomage' });
      setConfirm(null);
    } catch (err) {
      onError?.(err.message || 'Template fields could not be loaded.');
      setConfirm(null);
    } finally {
      setApplying(false);
    }
  };

  const openSaveDialog = () => {
    setSaveName('');
    setSaveOpen(true);
  };

  const closeSaveDialog = () => {
    if (!saving) setSaveOpen(false);
  };

  const confirmSave = async () => {
    const name = saveName.trim();
    if (!name) {
      onError?.('Enter a name for this template.');
      return;
    }
    setSaving(true);
    try {
      const body = section === 'health'
        ? { name, templateType: 'health', health: savePayload?.health }
        : { name, templateType: 'pathology', pathology: savePayload?.pathology };
      await createMyPatientTemplate(body);
      await reloadSaved();
      setSaveOpen(false);
    } catch (err) {
      onError?.(err.message || 'Template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const selectedOption = selected?.source === 'saved'
    ? savedOptions.find((row) => row.templateId === selected.templateId) || null
    : findCharacterOption(options, { personaId: selected?.personaId })
      ? { ...findCharacterOption(options, { personaId: selected?.personaId }), source: 'anatomage' }
      : null;

  const copy = SECTION_COPY[section] || SECTION_COPY.health;
  const confirmName = confirm?.option?.name?.trim()
    || (confirm?.kind === 'anatomage' ? `this ${ANATOMAGE_PATIENT}` : 'this template');
  const confirmMessage = confirm?.kind === 'saved'
    ? copy.savedBody(confirmName)
    : copy.anatomageBody(confirmName);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: description ? 0.5 : 1 }}>
        <Typography variant="subtitle1" component="h3">
          {title}
        </Typography>
        {savePayload ? (
          <Button
            size="small"
            startIcon={<SaveTemplateIcon />}
            onClick={openSaveDialog}
            sx={{ flexShrink: 0 }}
          >
            Save as template
          </Button>
        ) : null}
      </Stack>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      ) : null}

      {(loadingSaved || loadingOptions) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">Loading templates…</Typography>
        </Box>
      )}

      {!loadingSaved && savedLoadError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{savedLoadError}</Typography>
      )}

      {!loadingOptions && loadError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{loadError}</Typography>
      )}

      {!loadingSaved && !loadingOptions && !savedLoadError && (
        <Autocomplete
          options={combinedOptions}
          value={selectedOption}
          onChange={handleSelect}
          loading={applying}
          disabled={applying || (savedOptions.length === 0 && options.length === 0)}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(left, right) => {
            if (left?.source === 'saved' && right?.source === 'saved') {
              return String(left.templateId) === String(right.templateId);
            }
            if (left?.source === 'anatomage' && right?.source === 'anatomage') {
              return String(left?.personaId) === String(right?.personaId);
            }
            return false;
          }}
          groupBy={(option) => (
            option.source === 'saved' ? 'Your saved templates' : ANATOMAGE_PATIENTS
          )}
          filterOptions={(list) => list}
          inputValue={search}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') setSearch(value);
          }}
          renderOption={(props, option) => {
            if (option.source === 'saved') {
              return (
                <Box component="li" {...props} key={option.templateId}>
                  <Typography variant="body2" noWrap>{option.name?.trim() || 'Saved template'}</Typography>
                </Box>
              );
            }
            return (
              <Box component="li" {...props} key={option.personaId}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.25 }}>
                  <PatientProfileAvatar
                    persona={{
                      id: option.personaId,
                      has_profile_picture: option.hasProfilePicture,
                      profile_picture_revision: option.profilePictureRevision,
                    }}
                    size={40}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.name?.trim() || ANATOMAGE_PATIENT}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {detailLines(option).map((line) => line.value).join(' · ') || 'No details'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Apply a template"
              placeholder="Your templates or Anatomage Patients…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {applying ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={search.trim() ? 'No templates match your search.' : 'No templates saved yet. Use Save as template or pick an Anatomage Patient.'}
        />
      )}

      <NotificationDialog
        open={Boolean(confirm)}
        onClose={closeConfirm}
        title={copy.title}
        message={confirmMessage}
        iconPreset="warning"
        buttons={['reject', 'accept']}
        buttonLabels={{ reject: 'Cancel', accept: applying ? 'Loading…' : 'Replace fields' }}
        disableBackdropClick={applying}
        closeOnAction={!applying}
        onAction={{
          accept: confirmApply,
        }}
      />

      <NotificationDialog
        open={saveOpen}
        onClose={closeSaveDialog}
        title={section === 'health' ? 'Save health template' : 'Save pathology template'}
        iconPreset="info"
        buttons={['reject', 'accept']}
        buttonLabels={{ reject: 'Cancel', accept: saving ? 'Saving…' : 'Save' }}
        disableBackdropClick={saving}
        closeOnAction={!saving}
        onAction={{ accept: confirmSave }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save the current {section === 'health' ? 'health tab' : 'pathology tab'} fields under a name you can reuse on other patients.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Template name"
          value={saveName}
          onChange={(event) => setSaveName(event.target.value)}
          disabled={saving}
        />
      </NotificationDialog>
    </Box>
  );
}
